import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import {
  disconnectOpenHumans,
  getOpenHumansStatus,
  importOpenHumansFile,
  listOpenHumansFiles,
  startOpenHumansConnect,
} from "@/lib/openhumans.functions";

export const Route = createFileRoute("/_authenticated/openhumans")({
  head: () => ({
    meta: [
      { title: "Open Humans — Understand Your Genome" },
      {
        name: "description",
        content:
          "Annotate your 23andMe, AncestryDNA or genome VCF file against 13 databases: ClinVar, drug response, SNPedia, GWAS and gene evidence.",
      },
    ],
  }),
  component: OpenHumansPage,
});

interface OhStatus {
  configured: boolean;
  connected: boolean;
  oh_project_member_id: string | null;
}

interface OhFile {
  id: string;
  basename: string;
  source?: string;
}

function OpenHumansPage() {
  const navigate = useNavigate();
  const statusFn = useServerFn(getOpenHumansStatus);
  const connectFn = useServerFn(startOpenHumansConnect);
  const filesFn = useServerFn(listOpenHumansFiles);
  const importFn = useServerFn(importOpenHumansFile);
  const disconnectFn = useServerFn(disconnectOpenHumans);

  const [status, setStatus] = useState<OhStatus | null>(null);
  const [files, setFiles] = useState<OhFile[]>([]);
  const [skipped, setSkipped] = useState(0);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [filesLoaded, setFilesLoaded] = useState(false);
  const [acting, setActing] = useState(false);

  const connected = status?.connected ?? false;

  const fetchStatus = useCallback(async () => {
    try {
      setStatus((await statusFn({})) as OhStatus);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "We couldn't load your Open Humans status.");
      // Fall back to "configured, not connected" so the Connect button (and its
      // own server-side guard) is the user's retry path, not a dead end.
      setStatus({ configured: true, connected: false, oh_project_member_id: null });
    }
  }, [statusFn]);

  // Load the connection status once on mount.
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // When connected, load the recognized file list for the picker (once).
  useEffect(() => {
    if (!connected || filesLoaded) return;
    let cancelled = false;
    filesFn({})
      .then((result) => {
        if (cancelled) return;
        const r = result as { files: OhFile[]; skipped: number };
        setFiles(r.files);
        setSkipped(r.skipped);
        setFilesError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setFiles([]);
        setFilesError(
          err instanceof Error ? err.message : "We couldn't read your Open Humans files.",
        );
      })
      .finally(() => {
        if (!cancelled) setFilesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [connected, filesLoaded, filesFn]);

  async function handleConnect() {
    setActing(true);
    try {
      const result = (await connectFn({})) as { configured: boolean; authorize_url: string | null };
      if (!result.configured || !result.authorize_url) {
        // Req 3a: fail visibly (a clear message), not a 500.
        toast.error("Open Humans connection is not configured yet. Please try again later.");
        setActing(false);
        return;
      }
      // Full-page redirect to Open Humans; it sends us back to /openhumans/callback.
      window.location.assign(result.authorize_url);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "We couldn't start the Open Humans connection.",
      );
      setActing(false);
    }
  }

  async function handleImport() {
    if (!selectedFileId) return;
    setActing(true);
    try {
      await importFn({ data: { oh_file_id: selectedFileId } });
      toast.success("Imported. We've queued your file for annotation.");
      navigate({ to: "/dashboard" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "We couldn't import that file.");
      setActing(false);
    }
  }

  async function handleDisconnect() {
    // "Disconnect" is an ERASE, per the registered promise on this very page.
    if (
      !window.confirm(
        "Disconnect Open Humans? As described above, we'll delete the imported file and your report. This can't be undone.",
      )
    ) {
      return;
    }
    setActing(true);
    try {
      await disconnectFn({});
      toast.success("Disconnected. The imported file and report were deleted.");
      setFiles([]);
      setSkipped(0);
      setSelectedFileId(null);
      setFilesError(null);
      setFilesLoaded(false); // if they reconnect, re-pull the (now empty) list
      await fetchStatus();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "We couldn't disconnect.");
      setActing(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-2xl flex-1 px-5 py-14">
        <h1 className="text-3xl">Open Humans</h1>
        <p className="mt-3 text-muted-foreground">
          Annotate your 23andMe, AncestryDNA or genome VCF file against 13 databases: ClinVar, drug
          response, SNPedia, GWAS and gene evidence.
        </p>

        {/* Verbatim registered description (project 3583) -- do not paraphrase. */}
        <div className="mt-8 space-y-4 leading-relaxed text-muted-foreground">
          <p>
            Understand Your Genome turns a genotype file you already have in Open Humans (23andMe,
            AncestryDNA, or a genome/exome VCF) into a searchable report of your own variants. Each
            variant is annotated against 13 databases: current ClinVar classifications,
            drug-response evidence (PharmGKB), SNPedia notes, Open Targets gene and GWAS evidence,
            GTEx expression, predicted effects and population frequencies.
          </p>
          <p>
            <strong className="font-medium text-foreground">How your data is handled:</strong> you
            choose one file to import. It is processed only on servers we operate ourselves, with no
            third-party AI or analytics service. Your report is private: only you and our small
            admin team (for support) can see it, and nobody else unless you invite them. We never
            pass your data to anyone else and never use it for research. When you disconnect, we
            delete the imported file, your report and anything we added to your Open Humans account.
          </p>
          <p>The report is for exploration and curiosity, not a medical diagnosis. It is free.</p>
        </div>

        {status === null ? (
          <p className="mt-10 text-sm text-muted-foreground">Loading…</p>
        ) : !status.configured ? (
          <div className="mt-10 rounded-2xl border border-border bg-card p-6">
            <p className="font-medium">Open Humans isn't configured yet</p>
            <p className="mt-2 text-sm text-muted-foreground">
              We haven't finished connecting our Open Humans project. Please check back a little
              later — nothing is wrong with your account.
            </p>
          </div>
        ) : !connected ? (
          <div className="mt-10">
            <Button
              className="w-full bg-accent text-accent-foreground hover:bg-accent/90 sm:w-auto"
              size="lg"
              onClick={handleConnect}
              disabled={acting}
            >
              {acting ? "Connecting…" : "Connect Open Humans"}
            </Button>
          </div>
        ) : (
          <div className="mt-10">
            <p className="font-medium">
              Connected as{" "}
              <span className="font-mono text-sm text-foreground">
                {status.oh_project_member_id ?? "unknown"}
              </span>
            </p>

            {!filesLoaded ? (
              <p className="mt-6 text-sm text-muted-foreground">Loading your files…</p>
            ) : filesError ? (
              <div className="mt-6 rounded-2xl border border-border bg-card p-6">
                <p className="font-medium">We couldn't list your files</p>
                <p className="mt-2 text-sm text-muted-foreground">{filesError}</p>
              </div>
            ) : files.length === 0 ? (
              <div className="mt-6 rounded-2xl border border-border bg-card p-6">
                <p className="font-medium">No supported files to import</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  We couldn't find a supported genotype file (23andMe, AncestryDNA, a genome/exome
                  VCF, or FamilyTreeDNA) in your Open Humans account.
                </p>
              </div>
            ) : (
              <div className="mt-6 space-y-2">
                <p className="text-sm font-medium">Choose one file to import</p>
                {files.map((file) => (
                  <label
                    key={file.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors ${
                      selectedFileId === file.id
                        ? "border-primary bg-primary/5"
                        : "border-border bg-card"
                    }`}
                  >
                    <input
                      type="radio"
                      name="openhumans-file"
                      value={file.id}
                      checked={selectedFileId === file.id}
                      onChange={() => setSelectedFileId(file.id)}
                      className="mt-1"
                    />
                    <span className="min-w-0">
                      <span className="block truncate font-mono text-sm">{file.basename}</span>
                      {file.source && (
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          {file.source}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
                {skipped > 0 && (
                  <p className="pt-1 text-xs text-muted-foreground">
                    {skipped} other file{skipped === 1 ? "" : "s"} in your Open Humans account{" "}
                    {skipped === 1 ? "isn't" : "aren't"} a supported genotype file
                    {skipped === 1 ? "" : "s"}, so {skipped === 1 ? "it" : "they"} wasn
                    {skipped === 1 ? "'t" : "t"} listed.
                  </p>
                )}
                <Button
                  className="mt-3"
                  disabled={!selectedFileId || acting}
                  onClick={handleImport}
                >
                  {acting ? "Importing…" : "Import this file"}
                </Button>
              </div>
            )}

            <Button
              variant="outline"
              className="mt-8 text-destructive hover:text-destructive"
              disabled={acting}
              onClick={handleDisconnect}
            >
              Disconnect
            </Button>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
